import {
    Alert,
    AlertIcon,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { translate } from '../../../i18n';
import { useIsOrgManager } from '../../auth/roles';
import { BatchCreateCourtBlockadesResultDto } from '../../../models/court/dto/batch-create-court-blockades.dto';
import { CourtTemporaryBlockadeReason } from '../../../models/court/dto/court-temporary-blockade.dto';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { GameDto } from '../../../models/game/dto/game.dto';
import MultiCourtPicker from '../../analytics/components/MultiCourtPicker';
import { useBatchCreateCourtBlockades } from '../hooks/useBatchCreateCourtBlockades';
import { useGetCourtsCalendar } from '../hooks/useGetCourtCalendar';
import {
    CancelCourtsDayAggregate,
    useCancelCourtsDay,
} from '../hooks/useCancelCourtDay';
import {
    browserTimezone,
    buildCourtDayPreview,
    classifyCancelFailure,
    dateInputFromDate,
    dayWindowFromDateInput,
    FULL_DAY_TIME_OF_DAY,
    linkedCourtsMissingFromSelection,
} from '../utils/cancel-court-day.utils';

/** Design system semantic tokens (dashboard/DESIGN.md). */
const DANGER = '#B8453A';
const WARNING = '#C4883A';

/** Local "HH:mm" for a failed game's start, or "#id" if it isn't loaded. */
function gameTimeLabel(games: GameDto[], gameId: number): string {
    const g = games.find((x) => x.id === gameId);
    if (!g) return `#${gameId}`;
    const d = new Date(g.startTime);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * What happened to the optional "also block the day" write.
 *
 * `null` means it was never attempted (the operator unticked it, or every
 * court's cancel errored outright). A result object means the server ANSWERED —
 * possibly with `skipped` entries. `'failed'` means the write was REFUSED.
 *
 * The third state exists because the first version of this modal had only two,
 * and `'failed'` silently took the `null` branch's meaning: "no blockade was
 * asked for" and "the blockade we asked for did not happen" are opposite facts
 * about whether the day still resells, and the refunds have already gone out by
 * the time either is true.
 */
type BlockadeOutcome = BatchCreateCourtBlockadesResultDto | 'failed' | null;

type Settled = {
    cancel: CancelCourtsDayAggregate;
    blockade: BlockadeOutcome;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    /** Courts the operator may act on. A single-element list hides the picker. */
    courts: CourtDto[];
    /** Pre-ticked courts — outdoor ones on the calendar page, or the current court. */
    initialCourtIds: number[];
    /** Day the operator is looking at. */
    initialDate: Date;
};

/**
 * MYS-45: pick a day and cancel + refund every still-upcoming game on the
 * selected courts, in one action — the rain-day flow ("tout annuler en un
 * click"), optionally blocking the day so the freed slots don't resell.
 *
 * Mounted from both the global courts calendar (multi-court) and a single
 * court's calendar tab. The preview fetches its OWN per-day calendar rather
 * than reusing the parent page's window, so it can never silently read
 * "0 matchs" because the operator picked a date outside what the parent loaded.
 */
export default function CancelCourtDayModal({
    isOpen,
    onClose,
    courts,
    initialCourtIds,
    initialDate,
}: Props) {
    const toast = useToast();
    // `POST payments/cancel-court-day/:courtId` is `hasOrganizationAdminAccess`.
    // Both openers are already behind `isOrgManager` and both now mount this
    // only while it is open, so the check is defence in depth — kept because a
    // future third opener is all it would take, and its sibling modals on the
    // same pages all carry the same internal check.
    const isOrgManager = useIsOrgManager();

    const [date, setDate] = useState(() => dateInputFromDate(initialDate));
    const [selectedCourtIds, setSelectedCourtIds] = useState(initialCourtIds);
    const [blockDay, setBlockDay] = useState(true);
    const [acknowledged, setAcknowledged] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [settled, setSettled] = useState<Settled | null>(null);

    // Reset to a clean form each time the modal (re)opens, seeded from whatever
    // the parent page is currently showing.
    const initialCourtIdsKey = initialCourtIds.join(',');
    const initialDateKey = dateInputFromDate(initialDate);
    useEffect(() => {
        if (!isOpen) return;
        setSettled(null);
        setAcknowledged(false);
        setProgress(null);
        setBlockDay(true);
        setDate(initialDateKey);
        setSelectedCourtIds(initialCourtIdsKey ? initialCourtIdsKey.split(',').map(Number) : []);
    }, [isOpen, initialCourtIdsKey, initialDateKey]);

    // Memoized so the query key (which embeds these Dates) is stable per day.
    const dayWindow = useMemo(() => {
        const { startTime, endTime } = dayWindowFromDateInput(date);
        return { start: new Date(startTime), end: new Date(endTime) };
    }, [date]);

    const calendarQueries = useGetCourtsCalendar(
        selectedCourtIds,
        dayWindow.start,
        dayWindow.end,
    );
    const isPreviewLoading = calendarQueries.some((q) => q.isLoading);
    const hasPreviewError = calendarQueries.some((q) => q.isError);

    const courtNameById = useMemo(
        () => new Map(courts.map((c) => [c.id, c.name])),
        [courts],
    );

    // Deliberately NOT memoized: the dependency list would have to include one
    // entry per selected court, and a dep array that changes SIZE between
    // renders is a React violation (it warns and silently keeps the old
    // comparison). The fold is a handful of array filters over a single day's
    // calendar, so recomputing per render is cheaper than getting this wrong.
    const preview = buildCourtDayPreview(
        selectedCourtIds.map((courtId, i) => ({
            courtId,
            courtName: courtNameById.get(courtId) ?? `#${courtId}`,
            data: calendarQueries[i]?.data,
        })),
        date,
    );

    const linkedWarnings = useMemo(
        () => linkedCourtsMissingFromSelection(selectedCourtIds, courts),
        [selectedCourtIds, courts],
    );

    // Kept in a ref so the blockade callback — which runs after the cancel
    // resolves — can finalize with the cancel aggregate it never receives.
    const cancelAggregateRef = useRef<CancelCourtsDayAggregate | null>(null);

    // The courts the blockade write was actually asked for. Needed by the
    // refusal render (to name what is still on sale) and by the blockade-only
    // retry, neither of which can recompute it: `selectedCourtIds` may have
    // moved, and the aggregate only says which cancels errored.
    const blockedCourtIdsRef = useRef<number[]>([]);

    const finalize = (
        cancel: CancelCourtsDayAggregate,
        blockade: BlockadeOutcome,
    ) => {
        setProgress(null);
        const cleanSweep =
            cancel.failedCount === 0 &&
            cancel.erroredCourtIds.length === 0 &&
            (blockade === null ||
                (blockade !== 'failed' && blockade.skipped.length === 0));
        if (cleanSweep) {
            toast({
                title: translate('court.cancel_day_success', {
                    count: cancel.canceledCount,
                }),
                status: 'success',
                duration: 5000,
                position: 'top-right',
            });
            onClose();
            return;
        }
        // Anything imperfect keeps the modal open. A silently-skipped blockade
        // is the exact failure this flow exists to prevent: the operator would
        // walk away believing the day is closed while slots still resell.
        setSettled({ cancel, blockade });
    };

    const blockadeMutation = useBatchCreateCourtBlockades(
        (result) => {
            // Only ever reached from the cancel's own result handler below, so
            // the ref is set — but a throw here would strand the operator on a
            // spinner after the refunds already went out, so degrade instead of
            // asserting.
            const cancel = cancelAggregateRef.current;
            if (!cancel) return;
            finalize(cancel, result);
        },
        () => {
            // The refunds are already out and the day is NOT blocked. Before
            // this existed the hook's bare "Error" toast was the whole report:
            // `finalize` was never called, so `progress` stayed frozen on the
            // last court, the result view never opened, and the only button left
            // was "Cancel & refund" — which re-runs the refunds rather than
            // retrying the blockade.
            const cancel = cancelAggregateRef.current;
            if (!cancel) return;
            finalize(cancel, 'failed');
        },
    );

    const cancelMutation = useCancelCourtsDay({
        onProgress: (done, total) => setProgress({ done, total }),
        onResult: (aggregate) => {
            cancelAggregateRef.current = aggregate;
            // Only block courts whose cancel actually completed — blocking a
            // court that still holds live games would just be skipped upstream.
            const blockableCourtIds = selectedCourtIds.filter(
                (id) => !aggregate.erroredCourtIds.includes(id),
            );
            if (!blockDay || blockableCourtIds.length === 0) {
                finalize(aggregate, null);
                return;
            }
            blockedCourtIdsRef.current = blockableCourtIds;
            blockadeMutation.mutate({
                courtIds: blockableCourtIds,
                dates: [date],
                timeOfDay: FULL_DAY_TIME_OF_DAY,
                timezone: browserTimezone(),
                reason: CourtTemporaryBlockadeReason.OTHER,
                description: translate('court.cancel_day_block_description'),
            });
        },
        onError: () => {
            setProgress(null);
            toast({
                title: translate('court.cancel_day_error'),
                status: 'error',
                duration: 5000,
                position: 'top-right',
            });
        },
    });

    const isWorking = cancelMutation.isLoading || blockadeMutation.isLoading;

    const submit = () => {
        cancelAggregateRef.current = null;
        blockedCourtIdsRef.current = [];
        cancelMutation.mutate({
            courtIds: selectedCourtIds,
            ...dayWindowFromDateInput(date),
        });
    };

    /**
     * Retry ONLY the blockade. `submit` would re-run the cancel, and the games
     * are already canceled and refunded — the server answers
     * `already_canceled` per game, so a full re-run turns a recoverable
     * one-click retry into a screen of failures over work that succeeded.
     */
    const retryBlockade = () => {
        blockadeMutation.mutate({
            courtIds: blockedCourtIdsRef.current,
            dates: [date],
            timeOfDay: FULL_DAY_TIME_OF_DAY,
            timezone: browserTimezone(),
            reason: CourtTemporaryBlockadeReason.OTHER,
            description: translate('court.cancel_day_block_description'),
        });
    };

    const hasGames = preview.gameCount > 0;
    const canSubmit =
        selectedCourtIds.length > 0 &&
        hasGames &&
        !isPreviewLoading &&
        !hasPreviewError &&
        acknowledged;

    if (!isOrgManager) return null;

    // Hide the picker ONLY in the single-court context where that court is
    // already selected (the court-details page). With one court left unselected
    // — a calendar filtered down to one indoor court, which is not pre-ticked —
    // hiding it would leave the operator staring at "select at least one court"
    // with nothing to click.
    const showPicker = !(courts.length === 1 && selectedCourtIds.length === 1);

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    {translate('court.cancel_day_title')}
                    {courts.length === 1 ? ` — ${courts[0].name}` : ''}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {settled ? (
                        <VStack align="stretch" spacing={3} data-testid="cancel-day-result">
                            {settled.cancel.canceledCount > 0 && (
                                <Text>
                                    {translate('court.cancel_day_result_canceled', {
                                        count: settled.cancel.canceledCount,
                                    })}
                                </Text>
                            )}

                            {settled.cancel.erroredCourtIds.length > 0 && (
                                <VStack align="stretch" spacing={1}>
                                    <Text fontWeight="600" color={DANGER}>
                                        {translate('court.cancel_day_result_court_errors', {
                                            count: settled.cancel.erroredCourtIds.length,
                                        })}
                                    </Text>
                                    {settled.cancel.erroredCourtIds.map((courtId) => (
                                        <Text key={courtId} fontSize="sm">
                                            {courtNameById.get(courtId) ?? `#${courtId}`}
                                        </Text>
                                    ))}
                                </VStack>
                            )}

                            {settled.cancel.failedCount > 0 && (
                                <VStack align="stretch" spacing={1}>
                                    <Text fontWeight="600" color={WARNING}>
                                        {translate('court.cancel_day_result_failed_title', {
                                            count: settled.cancel.failedCount,
                                        })}
                                    </Text>
                                    {settled.cancel.perCourt.map((court) =>
                                        (court.result?.failed ?? []).map((f) => (
                                            <Text key={`${court.courtId}-${f.gameId}`} fontSize="sm">
                                                {courtNameById.get(court.courtId) ?? `#${court.courtId}`}{' '}
                                                {gameTimeLabel(preview.allGames, f.gameId)} —{' '}
                                                {translate(
                                                    `court.cancel_day_failure_${classifyCancelFailure(
                                                        f.error,
                                                    )}` as never,
                                                )}
                                            </Text>
                                        )),
                                    )}
                                </VStack>
                            )}

                            {settled.blockade === 'failed' && (
                                <VStack align="stretch" spacing={1} data-testid="cancel-day-block-failed">
                                    <Text fontWeight="600" color={DANGER}>
                                        {`${translate('court.cancel_day_block_too')} — ${translate(
                                            'common.unexpected_error',
                                        )}`}
                                    </Text>
                                    {blockedCourtIdsRef.current.map((courtId) => (
                                        <Text key={courtId} fontSize="sm">
                                            {courtNameById.get(courtId) ?? `#${courtId}`}
                                        </Text>
                                    ))}
                                </VStack>
                            )}

                            {settled.blockade &&
                                settled.blockade !== 'failed' &&
                                settled.blockade.skipped.length > 0 && (
                                <VStack align="stretch" spacing={1} data-testid="cancel-day-block-skipped">
                                    <Text fontWeight="600" color={WARNING}>
                                        {translate('court.cancel_day_result_block_skipped', {
                                            count: settled.blockade.skipped.length,
                                        })}
                                    </Text>
                                    {settled.blockade.skipped.map((s) => (
                                        <Text key={`${s.courtId}-${s.date}`} fontSize="sm">
                                            {courtNameById.get(s.courtId) ?? `#${s.courtId}`}
                                        </Text>
                                    ))}
                                </VStack>
                            )}
                        </VStack>
                    ) : (
                        <VStack align="stretch" spacing={4}>
                            <Text fontSize="sm" color="gray.600">
                                {translate('court.cancel_day_description')}
                            </Text>

                            <FormControl>
                                <FormLabel>{translate('court.cancel_day_date_label')}</FormLabel>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </FormControl>

                            {showPicker && (
                                <FormControl>
                                    <FormLabel>{translate('court.cancel_day_courts_label')}</FormLabel>
                                    <MultiCourtPicker
                                        courts={courts}
                                        selectedCourtIds={selectedCourtIds}
                                        onChange={setSelectedCourtIds}
                                    />
                                </FormControl>
                            )}

                            {isPreviewLoading ? (
                                <Text fontWeight="600" data-testid="cancel-day-preview-loading">
                                    <Spinner size="xs" mr={2} />
                                    {translate('court.cancel_day_loading_preview')}
                                </Text>
                            ) : hasPreviewError ? (
                                <Alert status="error" data-testid="cancel-day-preview-error">
                                    <AlertIcon />
                                    {translate('court.cancel_day_preview_error')}
                                </Alert>
                            ) : (
                                <Text fontWeight="600" data-testid="cancel-day-preview">
                                    {selectedCourtIds.length === 0
                                        ? translate('court.cancel_day_no_court')
                                        : hasGames
                                            ? translate('court.cancel_day_summary', {
                                                games: preview.gameCount,
                                                players: preview.playerCount,
                                                courts: selectedCourtIds.length,
                                            })
                                            : translate('court.cancel_day_none')}
                                </Text>
                            )}

                            {(preview.coachingSessionCount > 0 || preview.eventCount > 0) && (
                                <Text fontSize="sm" color={WARNING} data-testid="cancel-day-not-canceled">
                                    {translate('court.cancel_day_not_canceled_note', {
                                        coachings: preview.coachingSessionCount,
                                        events: preview.eventCount,
                                    })}
                                </Text>
                            )}

                            {linkedWarnings.map((w) => (
                                <Text
                                    key={w.courtId}
                                    fontSize="sm"
                                    color={WARNING}
                                    data-testid="cancel-day-linked-warning"
                                >
                                    {translate('court.cancel_day_linked_warning', {
                                        court: w.courtName,
                                        linked: w.linked.map((l) => l.name).join(', '),
                                    })}
                                </Text>
                            ))}

                            <Divider />

                            <Checkbox
                                isChecked={blockDay}
                                onChange={(e) => setBlockDay(e.target.checked)}
                                data-testid="cancel-day-block-toggle"
                            >
                                <Text fontSize="sm">{translate('court.cancel_day_block_too')}</Text>
                            </Checkbox>

                            {hasGames && (
                                <Checkbox
                                    isChecked={acknowledged}
                                    onChange={(e) => setAcknowledged(e.target.checked)}
                                    data-testid="cancel-day-ack"
                                >
                                    <Text fontSize="sm" fontWeight="600">
                                        {translate('court.cancel_day_confirm_ack', {
                                            games: preview.gameCount,
                                            players: preview.playerCount,
                                            courts: selectedCourtIds.length,
                                        })}
                                    </Text>
                                </Checkbox>
                            )}

                            {progress && (
                                <Text fontSize="sm" color="gray.600" data-testid="cancel-day-progress">
                                    {translate('court.cancel_day_progress', {
                                        done: progress.done,
                                        total: progress.total,
                                    })}
                                </Text>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter gap={3}>
                    <Button variant="ghost" onClick={onClose}>
                        {translate('close')}
                    </Button>
                    {settled ? (
                        // A REFUSED blockade is the one failure here that is
                        // both retryable and cheap, so it takes the button —
                        // and it must not fall through to `submit`, which would
                        // re-run refunds that already went out. A `skipped`
                        // blockade stays without a retry (the slot is booked or
                        // already blocked; asking again skips again).
                        settled.blockade === 'failed' ? (
                            <Button
                                colorScheme="red"
                                onClick={retryBlockade}
                                isLoading={isWorking}
                                data-testid="cancel-day-retry-blockade"
                            >
                                {translate('court.cancel_day_retry')}
                            </Button>
                        ) : (
                            (settled.cancel.failedCount > 0 ||
                                settled.cancel.erroredCourtIds.length > 0) && (
                                <Button
                                    colorScheme="red"
                                    onClick={submit}
                                    isLoading={isWorking}
                                    data-testid="cancel-day-retry"
                                >
                                    {translate('court.cancel_day_retry')}
                                </Button>
                            )
                        )
                    ) : (
                        <Button
                            colorScheme="red"
                            onClick={submit}
                            isLoading={isWorking}
                            isDisabled={!canSubmit}
                            data-testid="cancel-day-confirm"
                        >
                            {translate('court.cancel_day_confirm')}
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
